import LeavesApprovalsTable from "components/leaveApprovals/parts/LeaveApprovalsTable";
import { CardLayout } from "components/CardLayout";
import LeaveRequestsLoader, {
  useLeaveRequestsLoader,
} from "./Loaders/LeaveRequestsLoader";
import { SearchControls } from "./parts/SearchControls";
import { useMemo } from "react";
import { PaginationType, addToast } from "@octano/global-ui";
import { ITEMS_PER_PAGE_DEFAULT } from "./types";
import { annulLeaveRequest, getData } from "./api";
import * as XLSX from "xlsx";
import dayjs from "dayjs";
import { fetchAllUsers } from "views/LeavesReview/api";
import { splitDateRangeFilters } from "./dateRangeFilters";

function capitalize(string: string) {
  if (!string) return "";
  return string
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

const LeaveRequestsContent: React.FC = () => {
  const { data, loading, query, updateQuery, refresh } =
    useLeaveRequestsLoader();

  const pagination = useMemo<PaginationType | undefined>(() => {
    if (data) {
      return {
        totalItems: data.total,
        onChangePage: (page) => updateQuery({ ...query, page }),
        itemsPerPage: ITEMS_PER_PAGE_DEFAULT,
        totalPages: data.total_pages,
        currentPage: query.page,
      };
    } else return undefined;
  }, [data, query, updateQuery]);

  const handleExport = async () => {
    try {
      const { dateRange } = splitDateRangeFilters(query.searchParams);

      if (query?.searchParams?.startDate) {
        query.searchParams.startDate = dateRange.startDate;
      }

      if (query?.searchParams?.endDate) {
        query.searchParams.endDate = dateRange.endDate;
      }
      const [response, usersResponse] = await Promise.all([
        getData(query.searchParams),
        fetchAllUsers(),
      ]);

      if (response.error || !response.data) {
        addToast({
          text: "Error al descargar el reporte",
          color: "danger",
          icon: "error",
        });
        return;
      }

      const users = usersResponse.data || [];
      const usersMap = new Map(
        users.map((u) => [u.full_name.toLowerCase(), u]),
      );

      const exportData = response.data.map((item) => {
        const user = usersMap.get(item.requestBy.toLowerCase());
        const rut = user
          ? `${user.rut}-${user.findUserOrds?.data?.[0]?.dv || ""}`
          : "";

        return {
          "Fecha de solicitud": dayjs(item.createdAt).format("DD/MM/YYYY"),
          "Tipo de solicitud": capitalize(item.requestType),
          "Total días hábiles": item.isFullDay ? item.requestedDays : 0.5,
          "Horario Medio Día": !item.isFullDay
            ? item.isMorning
              ? "Mañana"
              : "Tarde"
            : "",
          "Motivo ": item.comment,
          "Solicitado por": capitalize(item.requestBy),
          RUT: rut,
          "Solicitado a": capitalize(item.requestTo),
          "Fecha de inicio": dayjs(item.startDate).format("DD/MM/YYYY"),
          "Fecha de fin": item.endDate
            ? dayjs(item.endDate).format("DD/MM/YYYY")
            : "",
          Estado:
            item.approved === true
              ? "Aprobado"
              : item.approved === false
                ? "Rechazado"
                : "Pendiente",
          "Resuelto por": capitalize(item.doneBy || ""),
        };
      });

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Solicitudes");

      XLSX.writeFile(wb, "reporte_solicitudes.xlsx");
    } catch (error) {
      console.error(error);
      addToast({
        text: "Error al descargar el reporte",
        color: "danger",
        icon: "error",
      });
    }
  };

  const handleAnnul = async (id: string, payload?: { comments?: string }) => {
    try {
      const response = await annulLeaveRequest(id, payload);
      if (!response.error && response.data?.success) {
        addToast({
          text: "La solicitud ha sido anulada exitosamente.",
          color: "success",
          icon: "success",
        });
        refresh();
      } else {
        addToast({
          text: "Hubo un problema al anular la solicitud.",
          color: "danger",
          icon: "error",
        });
      }
    } catch (error) {
      console.error("Error al anular la solicitud", error);
      addToast({
        text: "Hubo un problema al anular la solicitud.",
        color: "danger",
        icon: "error",
      });
    }
  };

  return (
    <>
      <div className="text-light fw-400 fs-18 mb-5">
        A continuación se muestran las solicitudes de todos los colaboradores.
      </div>
      <SearchControls />
      <LeavesApprovalsTable
        data={data?.data || []}
        onRefresh={refresh}
        showReport={true}
        pagination={pagination}
        isLoading={loading}
        onExport={handleExport}
        handleAnnulRequest={handleAnnul}
      />
    </>
  );
};

const LeaveRequests: React.FC = () => {
  return (
    <section id="aprobaciones" className="pt-3">
      <CardLayout>
        <LeaveRequestsLoader>
          <LeaveRequestsContent />
        </LeaveRequestsLoader>
      </CardLayout>
    </section>
  );
};

export default LeaveRequests;
